/*
 * WebAuthn / passkey service wrapper.
 *
 * Talks to /api/v1/admin/webauthn/* endpoints. The 4 ceremony endpoints
 * (register-begin/complete, login-begin/complete) round-trip a challenge
 * + base64url-encoded credential data; @simplewebauthn/browser handles all
 * the ArrayBuffer ↔ base64url conversion so we just hand the server payload
 * to startRegistration() / startAuthentication() and pass the result back.
 */

import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';

import service from './service';

const TOKEN_KEY = 'accessToken';

const getToken = () => sessionStorage.getItem(TOKEN_KEY);

export const isPasskeySupported = () => browserSupportsWebAuthn();

/* State — public, no auth. Used to feature-detect on login screen. */
export const getWebauthnState = async () => {
  return await service(true, 'admin/webauthn/state', {}, null, false);
};

/* Enrollment ceremony. Returns { status, message, credential_id?, ... }. */
export const enrollPasskey = async (nickname) => {
  const begin = await service(
    false,
    'admin/webauthn/register/begin',
    { nickname: nickname || null },
    getToken(),
    false,
  );
  if (!begin?.status) return begin || { status: false, message: 'register/begin failed' };

  // Backend returns { status, options: {...}, challenge_id }.
  const options = begin.options;
  const challengeId = begin.challenge_id;
  if (!options || !challengeId) {
    return { status: false, message: 'Invalid response from server (missing options or challenge_id)' };
  }

  let attResp;
  try {
    attResp = await startRegistration({ optionsJSON: options });
  } catch (e) {
    if (e?.name === 'InvalidStateError') {
      return { status: false, message: 'This device is already enrolled.' };
    }
    if (e?.name === 'NotAllowedError') {
      return { status: false, message: 'Enrollment cancelled.' };
    }
    return { status: false, message: e?.message || 'Enrollment failed.' };
  }

  return await service(
    false,
    'admin/webauthn/register/complete',
    { credential: attResp, challenge_id: challengeId, nickname: nickname || null },
    getToken(),
    false,
  );
};

/* Login ceremony. Returns { status, access_token, data, ... } same shape as
 * the password+OTP login so the caller can persist it identically.
 *
 * email is optional:
 *   - present  → email-bound flow (user typed email + clicked the button).
 *                Backend enforces the credential belongs to that email.
 *   - omitted  → discoverable-credential flow (conditional UI picks one).
 *                Backend signs in as the credential's owner.
 * conditional: if true, run with mediation: 'conditional' so the passkey
 * appears in the browser's autofill UI rather than as a modal prompt.
 */
export const signInWithPasskey = async ({ email = null, conditional = false } = {}) => {
  const begin = await service(
    false,
    'admin/webauthn/login/begin',
    { email: email || null },
    null,
    false,
  );
  if (!begin?.status) return begin || { status: false, message: 'login/begin failed' };

  const options = begin.options;
  const challengeId = begin.challenge_id;
  if (!options || !challengeId) {
    return { status: false, message: 'Invalid response from server (missing options or challenge_id)' };
  }

  let assertion;
  try {
    assertion = await startAuthentication({
      optionsJSON: options,
      useBrowserAutofill: conditional,
    });
  } catch (e) {
    if (e?.name === 'NotAllowedError' || e?.name === 'AbortError') {
      return { status: false, message: 'Sign-in cancelled.', cancelled: true };
    }
    return { status: false, message: e?.message || 'Sign-in failed.' };
  }

  return await service(
    false,
    'admin/webauthn/login/complete',
    { email: email || null, credential: assertion, challenge_id: challengeId },
    null,
    false,
  );
};

/* Browser-level support detection for conditional UI.
 * Returns true only when both WebAuthn AND conditional mediation are available. */
export const isConditionalUIAvailable = async () => {
  if (!browserSupportsWebAuthn()) return false;
  if (!window.PublicKeyCredential) return false;
  if (typeof window.PublicKeyCredential.isConditionalMediationAvailable !== 'function') return false;
  try {
    return await window.PublicKeyCredential.isConditionalMediationAvailable();
  } catch (e) {
    return false;
  }
};

/* Management — admin's own credentials. Backend exposes this as GET. */
export const listMyPasskeys = async () => {
  return await service(true, 'admin/webauthn/list', {}, getToken(), false);
};

export const revokePasskey = async (credentialId) => {
  return await service(
    false,
    'admin/webauthn/revoke',
    { credential_id: credentialId },
    getToken(),
    false,
  );
};

/* Super-admin: revoke all of another admin's passkeys. */
export const adminResetPasskeys = async (targetAdminId, reason) => {
  return await service(
    false,
    'admin/webauthn/admin-reset',
    { admin_id: targetAdminId, reason: reason || '' },
    getToken(),
    false,
  );
};
